/**
 * Robust utility for authenticated fetch with automatic token refresh and login redirect
 */

import { buildApiUrl, API_ENDPOINTS } from '../config/api';

// Constants
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  TOKEN: 'token',
  REFRESH_TOKEN: 'refresh_token',
};

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

const ERROR_MESSAGES = {
  REFRESH_FAILED: 'Refresh failed',
  NETWORK_ERROR: 'Network error',
  UNAUTHORIZED: 'Unauthorized access',
};

const REDIRECT_PATH = '/authentication/sign-in';
const MAX_RETRIES = 1;

// State management for token refresh
let isRefreshing = false;
let refreshPromise = null;

/**
 * Get access token from localStorage
 * @returns {string|null} The access token
 */
function getAccessToken() {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || localStorage.getItem(STORAGE_KEYS.TOKEN);
}

/**
 * Check if token is expired (basic check)
 * @param {string} token - The token to check
 * @returns {boolean} True if token is expired
 */
function isTokenExpired(token) {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Add 5 minute buffer to refresh token before it actually expires
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    return (payload.exp * 1000) < (Date.now() + bufferTime);
  } catch {
    return false; // If we can't parse, assume it's valid
  }
}

/**
 * Set access token in localStorage
 * @param {string} token - The access token to store
 */
function setAccessToken(token) {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
}

/**
 * Clear all tokens from localStorage
 */
function clearTokens() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
}

/**
 * Refresh the access token using the refresh token
 * @returns {Promise<string>} The new access token
 */
async function refreshAccessToken() {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  
  isRefreshing = true;
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  // Guard against invalid refresh token strings (e.g., 'undefined')
  const isRefreshTokenValid = !!(refreshToken && refreshToken.split('.').length === 3);
  
  if (!refreshToken) {
    clearTokens();
    window.location.href = REDIRECT_PATH;
    throw new Error(ERROR_MESSAGES.REFRESH_FAILED);
  }
  
  refreshPromise = fetch(buildApiUrl(API_ENDPOINTS.REFRESH_TOKEN), {
    method: "POST",
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (res) => {
      if (!res.ok) {
        if (res.status === HTTP_STATUS.UNAUTHORIZED) {
          clearTokens();
          window.location.href = REDIRECT_PATH;
        }
        throw new Error(ERROR_MESSAGES.REFRESH_FAILED);
      }
      const data = await res.json();
      setAccessToken(data.access_token);
      return data.access_token;
    })
    .catch((err) => {
      console.error('Refresh token request failed:', err);
      clearTokens();
      window.location.href = REDIRECT_PATH;
      throw err;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
    
  return refreshPromise;
}

/**
 * Fetch with authentication and automatic token refresh
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {boolean} isRetry - Whether this is a retry attempt
 * @returns {Promise<Response>} The fetch response
 */
export async function fetchWithAuth(url, options = {}, isRetry = false) {
  const accessToken = getAccessToken();
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  // Local validity check for refresh token format (must be a JWT)
  const isRefreshTokenValid = !!(refreshToken && refreshToken.split('.') && refreshToken.split('.').length === 3);
  
  // Check if token is expired before making request
  if (accessToken && isTokenExpired(accessToken) && isRefreshTokenValid && !isRetry) {
    try {
      const newAccessToken = await refreshAccessToken();
      options.headers = {
        ...(options.headers || {}),
        'Authorization': `Bearer ${newAccessToken}`,
        'Content-Type': options.headers && options.headers['Content-Type'] 
          ? options.headers['Content-Type'] 
          : 'application/json',
      };
    } catch (err) {
      console.error('Token refresh failed:', err);
      // Clear tokens and redirect to login on refresh failure
      clearTokens();
      window.location.href = REDIRECT_PATH;
      return Promise.reject(err);
    }
  } else {
    options.headers = {
      ...(options.headers || {}),
      'Authorization': accessToken ? `Bearer ${accessToken}` : '',
      'Content-Type': options.headers && options.headers['Content-Type'] 
        ? options.headers['Content-Type'] 
        : 'application/json',
    };
    
    if (!accessToken) {
      delete options.headers['Authorization'];
    }
  }

  // Build the full URL using the centralized configuration
  const fullUrl = url.startsWith('http') ? url : buildApiUrl(url);
  const response = await fetch(fullUrl, options);

  if (response.status === HTTP_STATUS.UNAUTHORIZED && isRefreshTokenValid && !isRetry && isRetry < MAX_RETRIES) {
    try {
      const newAccessToken = await refreshAccessToken();
      options.headers['Authorization'] = `Bearer ${newAccessToken}`;
      return fetchWithAuth(url, options, true);
    } catch (err) {
      console.error('Token refresh failed during request:', err);
      // Clear tokens and redirect to login on refresh failure
      clearTokens();
      window.location.href = REDIRECT_PATH;
      return Promise.reject(err);
    }
  }

  return response;
}

/**
 * Enhanced fetch with timeout and better error handling
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>} The fetch response
 */
export async function fetchWithTimeout(url, options = {}, timeout = 30000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
}

/**
 * Utility to handle common HTTP errors
 * @param {Response} response - The fetch response
 * @returns {Promise<Response>} The response or throws error
 */
export function handleHttpErrors(response) {
  if (response.ok) {
    return response;
  }
  
  switch (response.status) {
    case HTTP_STATUS.UNAUTHORIZED:
      throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
    case HTTP_STATUS.FORBIDDEN:
      throw new Error('Access forbidden');
    case HTTP_STATUS.NOT_FOUND:
      throw new Error('Resource not found');
    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      throw new Error('Internal server error');
    default:
      throw new Error(`HTTP error! status: ${response.status}`);
  }
}

export default fetchWithAuth; 